// <purpose>The two residue words of the Statistik page — "failed" and "unclassified" — through ONE seam, so
// the Volumen je Kategorie residue columns, the donut's and the bars' „+n" hover words, and the Ungeklärt KPI
// sub-line can never drift apart.</purpose>
// <constraints>
//   failedWord = the chrome constant FAILED_WORD (no served face exists for it; the state tag keeps „Fehler").
//   unclassifiedWord = the served face of the vocabulary's unclassified value; a vocabulary that declares
//   none (WIM) uses the chrome word UNCLASSIFIED_FALLBACK_FACE once there is something to name
//   (`needed`), else null.
// </constraints>

// Chrome word (German-round item) for the unclassified line when the settled vocabulary declares no
// unclassified value (WIM) yet the window holds such mails.
export const UNCLASSIFIED_FALLBACK_FACE = 'Ohne Zuordnung';

// The slice of the resolved vocabulary-meta object (`cm`) the words need. This module imports nothing from the
// charts: charts/_volumeStack imports it (one direction).
type ResidueMeta = { categoryKeys: string[]; isUnclassified: (key: string) => boolean; label: (key: string) => string };

/** The unclassified value's key in the served vocabulary (the served `excludedFromEvaluation` marker), or null when the role declares none. */
export const unclassifiedKey = (cm: Pick<ResidueMeta, 'categoryKeys' | 'isUnclassified'>): string | null =>
  cm.categoryKeys.find(k => cm.isUnclassified(k)) ?? null;

// Chrome word for the failed class on the Statistik surfaces: the export's word. The state TAG keeps the
// short face (`STATUS_LABELS.pipeline_error` = „Fehler", tags abbreviate).
export const FAILED_WORD = 'Bearbeitungsfehler';

export const failedWord = (): string => FAILED_WORD;

// Chrome word for the third row's „approved as another kind" child (the standing claim's disagreement
// flag; only a role with several kinds can have one). No face family serves it yet; when one does, this
// constant is what gets replaced.
export const DEVIATING_WORD = 'Abweichend';

// Chrome word for the Offen card's unclassified child: the clerk's to-do (the classifier could not
// tell, the clerk classifies first). A next-best-step word on the active lens; the archive lens names
// the same rows by what remained unresolved (unclassifiedWord under Ungeklärt).
export const TO_ASSIGN_WORD = 'Zuzuordnen';

export const unclassifiedWord = (cm: ResidueMeta, needed: boolean): string | null => {
  const key = unclassifiedKey(cm);
  const face = key === null ? null : cm.label(key);
  return face ?? (needed ? UNCLASSIFIED_FALLBACK_FACE : null);
};

// The one grammar of a residue fact in prose: word first, count second, no case change
// ("Bearbeitungsfehler 3", "Ohne Zuordnung 2"). The Ungeklärt KPI sub-line and the Tag.Balken tooltip render through it.
export const failedText = (count: number): string => `${failedWord()} ${count}`;

// null = nothing to say: the vocabulary declares no unclassified value AND the count is 0 (the chrome
// fallback word only names a real, non-zero population). Callers render null as empty text / drop it.
export const unclassifiedText = (cm: ResidueMeta, count: number): string | null => {
  const word = unclassifiedWord(cm, count > 0);
  return word === null ? null : `${word} ${count}`;
};
